/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomLayout from "../../Core/Layout/CustomLayout";
import Header from "./Header";
import ChevronIcon from "../../Common/Svgs/ChevronIcon";
import { rentalColumns } from "./data";
import CustomTable from "../../Core/Components/CustomTable";
import RentalIcon from "../../Common/Svgs/RentalIcon";
import { getTableContainerHeight } from "../../Utils/getTableContainerHeight";
import { updateRental } from "../../Redux/Slices/rentals.slice";
import { getRentalList } from "../../Redux/APIs/rentalsAPI";
import useDebounce from "../../Core/hooks/useDebounce";
import ToastMessageModal from "./ToastMessageModal";
import MoveToHubStationModal from "./MoveToHubStationModal";
import AddBatteryEnergyModal from "./AddBatteryEnergyModal/AddBatteryEnergyModal";
import ViewBatteryEnergyModal from "./ViewBatteryEnergyModal";
import { getAllBatteryEnergyRates } from "../../Redux/APIs/ratesAPI";

const LOCAL_DEFAULT_LIMIT = 10;

const RentalScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    batteryEnergyModal,
    rentalList,
    moveToHubStationModal,
    showToastMessage,
    viewBatteryEnergyModal,
  } = useSelector((state) => state.rentals);

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    hubs: [],
    drivers: [],
    status: [],
  });

  const tableContainerRef = useRef();

  const debounceSearch = useDebounce(search, 500);

  // redirect to details page
  const handleRowClick = async (record) => {
    dispatch(updateRental({ key: "currentRental", value: record }));
    navigate(`/rentals/${record.id}`);
  };

  // get rentals list with filters
  useEffect(() => {
    const filterObj = {
      limit: LOCAL_DEFAULT_LIMIT,
      offset: ((currentPage || 1) - 1) * LOCAL_DEFAULT_LIMIT,
    };

    if (debounceSearch) filterObj.searchQuery = debounceSearch;
    if (filters.hubs.length) filterObj.hubs = filters.hubs;
    if (filters.status.length) filterObj.status = filters.status;

    dispatch(getRentalList(filterObj));
  }, [dispatch, currentPage, filters, debounceSearch]);

  // close the toast message after 5 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(
        updateRental({
          key: "showToastMessage",
          value: {
            visible: false,
            data: {},
          },
        }),
      );
    }, 5000);

    return () => clearTimeout(timeout);
  }, [dispatch, showToastMessage.visible]);

  // get latest rates
  useEffect(() => {
    // dispatch(getAllBatteriesV3());
    dispatch(getAllBatteryEnergyRates());
  }, [dispatch]);

  return (
    <CustomLayout pageName="Rentals">
      <div className="relative bg-background">
        <Header
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setCurrentPage={setCurrentPage}
        />

        <div
          className={`h-[calc(100vh-72px-68px-40px)] ${
            rentalList?.result?.length && "overflow-y-scroll"
          }`}
          ref={tableContainerRef}
        >
          <CustomTable
            disableScroll
            columns={rentalColumns}
            data={rentalList.result}
            onRowClick={handleRowClick}
            emptyState={<RentalIcon className="text-dark-grey-text" />}
            emptyStateHeight={getTableContainerHeight(tableContainerRef)}
            rowClassName="group cursor-pointer"
            pagination={{
              pageSize: LOCAL_DEFAULT_LIMIT,
              total: rentalList.totalRecords,
              showSizeChanger: false,
              onChange: (page) => {
                setCurrentPage(page);
              },
              current: currentPage,
              itemRender: (current, type, originalElement) => {
                const lastPage = rentalList.totalRecords / current;

                if (type === "prev" && current === 0) {
                  return (
                    <button
                      type="button"
                      className="-rotate-180 rounded-md h-[32px] min-w-[32px] bg-background flex items-center justify-center p-2"
                      style={{
                        pointerEvents: "none",
                      }}
                    >
                      <ChevronIcon className="stroke-dark-gray" />
                    </button>
                  );
                }

                if (type === "prev" && current > 0) {
                  return (
                    <button
                      type="button"
                      className="-rotate-180 rounded-md h-[32px] min-w-[32px] bg-background flex items-center justify-center p-2"
                    >
                      <ChevronIcon className="stroke-dark-gray" />
                    </button>
                  );
                }

                if (type === "next" && lastPage <= 10) {
                  return (
                    <button
                      type="button"
                      className="rounded-md h-[32px] min-w-[32px] bg-background flex items-center justify-center p-2"
                      style={{ pointerEvents: "none" }}
                    >
                      <ChevronIcon className="stroke-dark-gray" />
                    </button>
                  );
                }

                if (type === "next") {
                  return (
                    <button
                      type="button"
                      className="rounded-md h-[32px] min-w-[32px] bg-background flex items-center justify-center p-2"
                    >
                      <ChevronIcon className="stroke-dark-gray" />
                    </button>
                  );
                }

                return originalElement;
              },
            }}
          />

          <AddBatteryEnergyModal
            visible={batteryEnergyModal}
            onCancel={() =>
              dispatch(
                updateRental({
                  key: "batteryEnergyModal",
                  value: false,
                }),
              )
            }
          />

          <MoveToHubStationModal
            visible={moveToHubStationModal}
            onCancel={() =>
              dispatch(
                updateRental({
                  key: "moveToHubStationModal",
                  value: false,
                }),
              )
            }
          />

          <ViewBatteryEnergyModal
            visible={viewBatteryEnergyModal}
            onCancel={() =>
              dispatch(
                updateRental({
                  key: "viewBatteryEnergyModal",
                  value: false,
                }),
              )
            }
          />

          <ToastMessageModal
            visible={showToastMessage.visible}
            onCancel={() =>
              dispatch(
                updateRental({
                  key: "showToastMessage",
                  value: { visible: false, data: {} },
                }),
              )
            }
            title={showToastMessage?.data?.title}
            description={showToastMessage?.data?.description}
          />
        </div>
      </div>
    </CustomLayout>
  );
};

export default RentalScreen;
